import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { IntranetChannelList } from '@/products/intranet/components/intranet-channels';
import { IntranetGadgets } from '@/products/intranet/components/intranet-gadgets';
import { IntranetShortcutGrid } from '@/products/intranet/components/intranet-quick-access';
import { RAIL_MENU_OFFSET, railStickyTop } from '@/products/intranet/features/intranet/rail-sticky';
import { useIntranetChannelNav } from '@/products/intranet/features/intranet/use-intranet-channel-nav';
import { enabledIntranetWidgets } from '@/products/intranet/features/intranet/shortcut-icons';
import {
  useIntranetQuickAccess,
  useIntranetWidgets,
} from '@/products/intranet/features/intranet/use-intranet-directory';

/** Channels and Schnellzugriff beside the feed or an article. The page scrolls; the rails do not. */
export function IntranetColumns({ children }: { children: ReactNode }) {
  const channelNav = useIntranetChannelNav();
  const quickAccess = useIntranetQuickAccess();
  const widgets = useIntranetWidgets();

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)_280px] lg:gap-6">
      <StickyRail className="hidden lg:block">
        <IntranetChannelList {...channelNav} />
      </StickyRail>
      <div className="min-w-0">{children}</div>
      <StickyRail className="hidden lg:block">
        <aside aria-label="Schnellzugriff" className="min-w-0 space-y-4">
          <section className="min-w-0 space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-primary">Schnellzugriff</h2>
            {quickAccess.isPending ? (
              <p className="text-sm text-muted-foreground">Schnellzugriff wird geladen …</p>
            ) : (
              <IntranetShortcutGrid items={quickAccess.data?.items ?? []} />
            )}
          </section>
          <IntranetGadgets widgets={enabledIntranetWidgets(widgets.data?.items ?? [])} />
        </aside>
      </StickyRail>
    </div>
  );
}

/** Scrolls with the page until the column ends, then stays under the menus. */
function StickyRail({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(RAIL_MENU_OFFSET);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    const measure = () => {
      setTop(railStickyTop(node.offsetHeight, window.innerHeight, RAIL_MENU_OFFSET));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div ref={ref} className={cn('min-w-0 self-start lg:sticky', className)} style={{ top }}>
      {children}
    </div>
  );
}
